import { useCallback, useEffect, useRef, useState } from 'react';

import { applyCypressData, isUndefined } from 'Utilities/helpers';

const MAX_HEIGHT_VALUES = Object.freeze({
	NONE: 'none',
	ZERO: '0px',
});

/**
 * @param {object} [params]
 * @param {string | null} [params.id]
 * @param {boolean} [params.isExpanded]
 * @param {(expanded: boolean) => boolean | void} [params.onClick]
 */
const useExpandable = ({ id, isExpanded = false, onClick } = {}) => {
	const [expanded, setExpanded] = useState(isExpanded);
	const [maxHeight, setMaxHeight] = useState(() => (isExpanded ? MAX_HEIGHT_VALUES.NONE : MAX_HEIGHT_VALUES.ZERO));

	const ready = useRef();
	const contentRef = useRef(null);
	const expandedRef = useRef(expanded);
	const setMaxHeightRef = useRef();

	// To only mount `transitionend` event once, we use ref to keep expanded value
	expandedRef.current = expanded;

	const setExpandableMaxHeight = useCallback(() => {
		const element = contentRef?.current;

		// Paddings, margins and borders within the expandable make getting the absolute height of the element difficult.
		// Taking both wrapper and content `scrollHeight` allows to accurately expand the component.
		const contentHeight = Math.max(element?.scrollHeight, element?.parentElement.scrollHeight);

		const nextMaxHeight = contentHeight ? `${contentHeight}px` : MAX_HEIGHT_VALUES.NONE;

		setMaxHeight(nextMaxHeight);
	}, []);

	const updateExpandableState = useCallback(
		(nextExpanded) => {
			setExpandableMaxHeight();
			setExpanded(nextExpanded);
		},
		[setExpandableMaxHeight],
	);

	const handleClick = () => {
		const expand = onClick(!expanded);
		const nextExpanded = !isUndefined(expand) ? expand : !expanded;

		// Calculating maxHeight onClick event removed need for any resize observers!
		updateExpandableState(nextExpanded);
	};

	useEffect(() => {
		/**
		 * After setting maxHeight in onClick handler, this useEffect sets it to 0, to perform CSS transition.
		 * We use `requestAnimationFrame` here to make sure browser will pickup absolute height change, and then
		 * in next frame, it will pickup change to 0 and start transition.
		 */
		if (ready.current && !expanded) {
			setMaxHeightRef.current = requestAnimationFrame(() => {
				setMaxHeight(MAX_HEIGHT_VALUES.ZERO);
			});
		}

		return () => cancelAnimationFrame(setMaxHeightRef.current);
	}, [expanded]);

	useEffect(() => {
		if (!ready.current) {
			return;
		}

		if (isExpanded !== expandedRef.current) {
			updateExpandableState(isExpanded);
		}
	}, [isExpanded, updateExpandableState]);

	useEffect(() => {
		const ref = contentRef?.current?.parentElement;

		const onTransitionEnd = () => {
			if (expandedRef.current) {
				// To allow expandable to have variable height we set maxHeight to none as soon as CSS Transition ends!
				setMaxHeight(MAX_HEIGHT_VALUES.NONE);
			}
		};

		ref?.addEventListener?.('transitionend', onTransitionEnd, { passive: true });

		return () => {
			ref?.removeEventListener?.('transitionend', onTransitionEnd, { passive: true });
		};
	}, []);

	useEffect(() => {
		ready.current = true;
	}, []);

	const buttonProps = {
		'aria-controls': id ?? undefined,
		'aria-expanded': expanded,
		...applyCypressData(`${id}Button`),
		onClick: handleClick,
		tabIndex: 0,
	};

	return {
		buttonProps,
		contentRef,
		expanded,
		maxHeight,
	};
};

export default useExpandable;
